import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { WATER_IMG, formatXCD } from "@/lib/constants";
import { Droplets, Moon, Mountain } from "lucide-react";

export default function WaterDeliveryPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [hillAreas, setHillAreas] = useState([]);
  const [afterHoursCfg, setAfterHoursCfg] = useState({ fee: 0, start_time: "18:00", active: true });
  const [form, setForm] = useState({
    sku: "", address: "", delivery_date: "", delivery_time: "", notes: "", hill_area_id: "",
  });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useLiveData(() => {
    api.get("/pricing").then(r => {
      const active = (r.data.water_tiers || []).filter(t => t.active !== false);
      setTiers(active);
      setHillAreas((r.data.hill_areas || []).filter(a => a.active !== false));
      setAfterHoursCfg(r.data.after_hours || { fee: 0, start_time: "18:00", active: true });
      // Preselect first active tier if none set
      setForm(f => f.sku ? f : { ...f, sku: active[0]?.sku || "" });
    }).catch(() => {});
  });

  // Auto-detect after-hours based on time and config
  const isAfterHours = useMemo(() => {
    if (!form.delivery_time || !afterHoursCfg?.active) return false;
    const [dh, dm] = form.delivery_time.split(":").map(Number);
    const [sh, sm] = String(afterHoursCfg.start_time || "18:00").split(":").map(Number);
    return (dh * 60 + dm) >= (sh * 60 + sm);
  }, [form.delivery_time, afterHoursCfg]);

  // Live server-side quote whenever inputs change
  useEffect(() => {
    if (!form.sku) { setQuote(null); return; }
    let cancelled = false;
    api.post("/pricing/water/quote", {
      sku: form.sku,
      hill_area_id: form.hill_area_id || null,
      delivery_time: form.delivery_time || null,
    }).then(r => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [form.sku, form.hill_area_id, form.delivery_time]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to book"); return nav("/login"); }
    if (!form.sku) { toast.error("Choose a delivery size"); return; }
    setSubmitting(true);
    try {
      const payload = {
        sku: form.sku,
        address: form.address,
        delivery_date: form.delivery_date,
        delivery_time: form.delivery_time,
        hill_area_id: form.hill_area_id || null,
        notes: form.notes,
      };
      const { data } = await api.post("/bookings/water", payload);
      toast.success(`Water delivery booked — ${data.invoice_number}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to book");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={WATER_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1D4ED8]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Service · Bulk Water Delivery</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-2">Clean water, on time.</h1>
          <p className="mt-3 max-w-xl text-white/90">Choose your quantity, schedule a slot, and we'll fill your tank.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#1D4ED8]"><Droplets className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline">Book now</span></div>
          <h2 className="font-heading font-black text-2xl mt-1">Water Delivery booking</h2>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-sm font-bold text-slate-900 mb-2 block">Delivery size</Label>
              <RadioGroup value={form.sku} onValueChange={(v) => setForm({ ...form, sku: v })}
                          className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tiers.map((t) => (
                  <label key={t.sku}
                         data-testid={`water-tier-${t.sku}`}
                         className={`cursor-pointer border-2 rounded-md p-3 text-center transition ${
                           form.sku === t.sku ? "border-[#E32636] bg-red-50" : "border-slate-200 hover:border-slate-400"
                         }`}>
                    <RadioGroupItem value={t.sku} className="sr-only" />
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{t.label}</div>
                    <div className="text-sm font-bold text-[#1D4ED8] mt-1">{formatXCD(t.price)}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900">Delivery address</Label>
              <Input data-testid="water-address" required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900 flex items-center gap-1">
                <Mountain className="h-4 w-4 text-amber-600" /> Hill / steep area (optional)
              </Label>
              <Select value={form.hill_area_id || "__none"} onValueChange={(v) => setForm({ ...form, hill_area_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="water-hill-area"><SelectValue placeholder="Not a hill area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Not a hill area</SelectItem>
                  {hillAreas.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · +{formatXCD(a.surcharge)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">
                Choose your area if you're in Sugar Ridge, Galleon Bay Heights, Willoughby Bay, or other steep/hilly zones. A surcharge applies.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Date</Label>
                <Input data-testid="water-date" type="date" required value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Time</Label>
                <Input data-testid="water-time" type="time" required value={form.delivery_time} onChange={e => setForm({ ...form, delivery_time: e.target.value })} />
                {isAfterHours && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <Moon className="h-3 w-3" /> After-hours fee applies (after {afterHoursCfg.start_time})
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-900">Notes</Label>
              <Textarea data-testid="water-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Gate code, tank location, etc." />
            </div>

            {/* Live breakdown */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-1 text-sm">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Price breakdown</div>
              {quote ? (
                <>
                  <div className="flex justify-between text-slate-700">
                    <span>{quote.label}</span>
                    <span data-testid="water-base-price">{formatXCD(quote.base_price)}</span>
                  </div>
                  {quote.hill_surcharge > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Hill surcharge · {quote.hill_area_name}</span>
                      <span data-testid="water-hill-surcharge">{formatXCD(quote.hill_surcharge)}</span>
                    </div>
                  )}
                  {quote.after_hours && quote.after_hours_fee > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>After-hours fee</span>
                      <span data-testid="water-after-hours-fee">{formatXCD(quote.after_hours_fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-slate-900">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Total</span>
                    <span className="font-heading font-black text-2xl text-slate-900" data-testid="water-total">
                      {formatXCD(quote.total)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-slate-400 text-sm">Select a size to see your total.</div>
              )}
            </div>

            <Button type="submit" disabled={submitting || !quote} data-testid="water-submit-btn"
                    className="w-full bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-12">
              {submitting ? "Booking…" : "Confirm booking"}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
